/**
 * <ob-announcement-bar>: cycles the announcement messages with the prev/next arrows and an optional
 * autoplay timer. Autoplay pauses on hover and focus, stops after a tap and never runs under
 * prefers-reduced-motion. The track is an aria-live region that is silenced while autoplay runs.
 */
if (!customElements.get('ob-announcement-bar')) {
  customElements.define(
    'ob-announcement-bar',
    class AnnouncementBar extends HTMLElement {
      connectedCallback() {
        this.messages = Array.from(this.querySelectorAll('[data-announcement]'));
        this.track = this.querySelector('[data-announcement-track]');
        if (this.messages.length < 2 || !this.track) return;

        this.index = Math.max(
          0,
          this.messages.findIndex((message) => message.classList.contains('is-active'))
        );
        this.speed = (parseInt(this.dataset.speed, 10) || 5) * 1000;
        this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
        this.autoplay = this.dataset.autoplay === 'true' && !this.reducedMotion.matches;
        this.paused = false;

        this.querySelector('[data-announcement-prev]')?.addEventListener('click', () => this.step(-1));
        this.querySelector('[data-announcement-next]')?.addEventListener('click', () => this.step(1));

        if (!this.autoplay) return;

        this.track.setAttribute('aria-live', 'off');
        this.addEventListener('mouseenter', () => this.pause());
        this.addEventListener('mouseleave', () => this.resume());
        this.addEventListener('focusin', () => this.pause());
        this.addEventListener('focusout', (event) => {
          if (!this.contains(event.relatedTarget)) this.resume();
        });
        // A tap on a touch screen stops the rotation for good (there is no hover to pause it).
        this.addEventListener('pointerdown', (event) => {
          if (event.pointerType !== 'mouse') this.stop();
        });
        this.reducedMotion.addEventListener('change', () => {
          if (this.reducedMotion.matches) this.stop();
        });
        this.onVisibilityChange = () => (document.hidden ? this.clearTimer() : this.startTimer());
        document.addEventListener('visibilitychange', this.onVisibilityChange);

        if (Shopify.designMode) {
          document.addEventListener('shopify:block:select', (event) => this.onBlockSelect(event));
          document.addEventListener('shopify:block:deselect', () => this.resume());
        }

        this.startTimer();
      }

      disconnectedCallback() {
        this.clearTimer();
        if (this.onVisibilityChange) document.removeEventListener('visibilitychange', this.onVisibilityChange);
      }

      step(offset) {
        const length = this.messages.length;
        // Messages chosen with the arrows are announced; restart the timer so they get their full time.
        this.track.setAttribute('aria-live', 'polite');
        this.show((this.index + offset + length) % length);
        this.startTimer();
      }

      show(index) {
        if (index === this.index) return;
        const previous = this.messages[this.index];
        const next = this.messages[index];
        previous.classList.remove('is-active');
        previous.setAttribute('aria-hidden', 'true');
        previous.inert = true;
        next.classList.add('is-active');
        next.removeAttribute('aria-hidden');
        next.inert = false;
        this.index = index;
      }

      startTimer() {
        this.clearTimer();
        if (!this.autoplay || this.paused || this.stopped || document.hidden) return;
        this.timer = window.setTimeout(() => {
          this.track.setAttribute('aria-live', 'off');
          this.show((this.index + 1) % this.messages.length);
          this.startTimer();
        }, this.speed);
      }

      clearTimer() {
        window.clearTimeout(this.timer);
      }

      pause() {
        this.paused = true;
        this.clearTimer();
      }

      resume() {
        if (this.contains(document.activeElement)) return;
        this.paused = false;
        this.startTimer();
      }

      stop() {
        this.stopped = true;
        this.clearTimer();
        this.track.setAttribute('aria-live', 'polite');
      }

      onBlockSelect(event) {
        const index = this.messages.indexOf(event.target);
        if (index === -1) return;
        this.pause();
        this.show(index);
      }
    }
  );
}
